import React, { useEffect, useMemo } from "react";
import { Marker, Polyline, CircleMarker, useMap } from "react-leaflet";
import L from "leaflet";
import { useSelector } from "react-redux";
import { useTheme } from "@mui/material";

import {
  selectSmartRoutingRoutes,
  selectSmartRoutingSoloOrders,
  selectSmartRoutingOrigin,
} from "../../store/actions/smartRoutingSlice";

/**
 * Polyline + stop-marker overlay for the dashboard map.
 * Renders only while the "Rotas" tab is active. Reads everything from the
 * smartRoutingSlice — when the operator drags an order between routes the
 * polylines update automatically.
 */
export default function SmartRoutingOverlay() {
  const theme = useTheme();
  const map = useMap();
  const alignment = useSelector((s) => s.dashboard.alignment);
  const routes = useSelector(selectSmartRoutingRoutes);
  const soloOrders = useSelector(selectSmartRoutingSoloOrders);
  const origin = useSelector(selectSmartRoutingOrigin);
  const activeRouteIdx = useSelector((s) => s.smartRouting.activeRouteIdx);

  const isActive = alignment === "smart-routes";

  const routeColors = useMemo(
    () => [
      theme.palette.warning.main,
      theme.palette.info.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.error.main,
    ],
    [theme]
  );

  // Auto-fit when entering this tab and when the suggestions change
  useEffect(() => {
    if (!isActive) return;
    const points = [
      ...(origin ? [origin] : []),
      ...routes.flatMap((r) =>
        r.orders.map((o) => [Number(o.lat), Number(o.lon)])
      ),
      ...soloOrders.map((o) => [Number(o.lat), Number(o.lon)]),
    ].filter(([la, lo]) => Number.isFinite(la) && Number.isFinite(lo));
    if (points.length < 2) return;
    map.fitBounds(L.latLngBounds(points), { padding: [60, 60] });
  }, [isActive, origin, routes, soloOrders, map]);

  if (!isActive || !origin) return null;

  return (
    <>
      {routes.map((route, i) => {
        const color = routeColors[i % routeColors.length];
        const isActiveRoute = i === activeRouteIdx;
        const path = [
          origin,
          ...route.orders.map((o) => [Number(o.lat), Number(o.lon)]),
          origin,
        ];
        return (
          <React.Fragment key={route.cluster_id}>
            <Polyline
              positions={path}
              pathOptions={{
                color,
                weight: isActiveRoute ? 4 : 2,
                opacity: isActiveRoute ? 0.95 : 0.4,
                dashArray: isActiveRoute ? null : "6,6",
              }}
            />
            {route.orders.map((o, j) => (
              <Marker
                key={o.order_id}
                position={[Number(o.lat), Number(o.lon)]}
                icon={stopIcon(theme, color, j + 1)}
              />
            ))}
          </React.Fragment>
        );
      })}

      {soloOrders.map((o) => (
        <CircleMarker
          key={`solo-${o.order_id}`}
          center={[Number(o.lat), Number(o.lon)]}
          radius={6}
          pathOptions={{
            color: o.urgent
              ? theme.palette.error.main
              : theme.palette.text.secondary,
            fillColor: o.urgent
              ? theme.palette.error.main
              : theme.palette.text.disabled,
            fillOpacity: 0.85,
            weight: 2,
          }}
        />
      ))}
    </>
  );
}

function stopIcon(theme, color, label) {
  const fg = theme.palette.getContrastText(color);
  const bg = theme.palette.background.paper;
  return L.divIcon({
    className: "smart-routing-stop-marker",
    html: `<div style="
      width:26px;height:26px;border-radius:50%;
      background:${color};
      border:2px solid ${bg};
      display:flex;align-items:center;justify-content:center;
      color:${fg};
      font-family:${theme.typography.fontFamily};
      font-size:11px;font-weight:700;
      box-shadow:0 2px 6px rgba(0,0,0,0.2);
    ">${label}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });
}
